import { useMemo } from 'react';

import uniqBy from 'lodash/uniqBy';
import { useFormState, useForm } from 'react-final-form';
import { SelectedInstanceType } from '@cast/types';

import {
  ControlWithDescription,
  InlineWithDescription,
} from 'components/form-layouts';

import { ProvisionNodesCloudConfigurationFormValues } from '../../types';

import { RffAutocomplete, RffSelect } from '@cast/shared-rff-ui';

import { Stack } from '@cast/design-system';

type InstanceConfigurationSectionProps = {
  availableInstances: SelectedInstanceType[];
  availabilityZoneHint?: string;
};

const NO_INSTANCE_TYPES_AVAILABLE = 'No instance types available';
const NO_AVAILABILITY_ZONES_AVAILABLE = 'No availability zones available';

export const InstanceConfigurationSection = ({
  availableInstances,
  availabilityZoneHint,
}: InstanceConfigurationSectionProps) => {
  const form = useForm();
  const { values } = useFormState<ProvisionNodesCloudConfigurationFormValues>({
    subscription: { values: true },
  });
  const { instanceType } = values;

  const handleInstanceTypeChange = () => {
    form.batch(() => {
      form.change('availabilityZone', undefined);
      form.change('gpuType', undefined);
      form.change('gpuCount', undefined);
      form.change('replicas', undefined);
      form.change('partitionSize', undefined);
    });
  };

  const handleAvailabilityZoneChange = () => {
    form.batch(() => {
      form.change('gpuType', undefined);
      form.change('gpuCount', undefined);
      form.change('replicas', undefined);
      form.change('partitionSize', undefined);
    });
  };

  const uniqueInstanceTypes = useMemo(
    () => uniqBy(availableInstances, 'name').map((i) => i.name ?? ''),
    [availableInstances]
  );

  const instanceTypeOptions = useMemo(
    () =>
      uniqueInstanceTypes.length > 0
        ? uniqueInstanceTypes
        : [NO_INSTANCE_TYPES_AVAILABLE],
    [uniqueInstanceTypes]
  );

  const availableAvailabilityZones = useMemo(() => {
    if (!availableInstances || !instanceType) {
      return [];
    }

    return Array.from(
      new Set(
        availableInstances
          .filter((i) => i.name === instanceType)
          .map((i) => i.zone ?? '')
      )
    );
  }, [availableInstances, instanceType]);

  return (
    <InlineWithDescription
      description={
        <ControlWithDescription label="Instance">
          Select the instance type and availability zone for the node.
        </ControlWithDescription>
      }
      controls={
        <Stack gap={16}>
          <RffAutocomplete
            name="instanceType"
            label="Instance type"
            inputProps={{
              placeholder: 'Select instance type',
            }}
            virtualization
            options={instanceTypeOptions}
            optionDisabled={(option: string) =>
              option === NO_INSTANCE_TYPES_AVAILABLE
            }
            onChange={handleInstanceTypeChange}
            testId="instance-type-select"
          />
          <RffSelect
            name="availabilityZone"
            label="Availability zone"
            inputProps={{
              placeholder: 'Select availability zone',
              hint: availabilityZoneHint,
            }}
            options={
              availableAvailabilityZones.length > 0
                ? availableAvailabilityZones
                : [NO_AVAILABILITY_ZONES_AVAILABLE]
            }
            disabled={!instanceType}
            optionDisabled={(option) =>
              option === NO_AVAILABILITY_ZONES_AVAILABLE
            }
            onChange={handleAvailabilityZoneChange}
            testId="availability-zone-select"
          />
        </Stack>
      }
    />
  );
};
